import { useState, useEffect } from "react";
import { Plus, CheckCircle2, Clock, XCircle, Trash2 } from "lucide-react";
import { invoiceService } from "../../../services/invoice.service";
import { motion } from "framer-motion";
import Drawer from "../../../ui/Drawer";

export default function PaymentSection({ invoice, updateField, onPaymentUpdate, autoExpand = false }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddPayment, setShowAddPayment] = useState(autoExpand);

  // Derive totals (mirror TotalsSection) so payment status is accurate even before save
  const items = invoice.items || [];
  const subtotal = items.reduce((sum, item) => {
    const base = (item.quantity || 0) * (item.rate || 0);
    const discount =
      item.discountType === "percentage"
        ? (base * (item.discount || 0)) / 100
        : item.discount || 0;
    return sum + Math.max(0, base - discount);
  }, 0);

  const clientState = invoice.client?.state || "Maharashtra";
  const sameState = clientState === "Maharashtra";
  let cgst = 0,
    sgst = 0,
    igst = 0;

  items.forEach((item) => {
    const base = Math.max(
      0,
      (item.quantity || 0) * (item.rate || 0) -
        ((item.discountType === "percentage"
          ? ((item.quantity || 0) * (item.rate || 0) * (item.discount || 0)) / 100
          : item.discount) || 0)
    );
    const tax = (base * (item.tax || 0)) / 100;
    if (sameState) {
      cgst += tax / 2;
      sgst += tax / 2;
    } else {
      igst += tax;
    }
  });

  const taxTotal = cgst + sgst + igst;
  const discountType = invoice.discountType || "flat";
  const discountValue = invoice.discount || 0;
  const discountAmount =
    discountType === "percentage" ? (subtotal * discountValue) / 100 : discountValue;
  const shipping = invoice.shipping || 0;
  const rounding = invoice.rounding || 0;
  const derivedTotal = subtotal + taxTotal - discountAmount + shipping + rounding;
  const total = invoice.total || derivedTotal;

  useEffect(() => {
    if (invoice.id) {
      loadPayments();
    }
  }, [invoice.id]);

  async function loadPayments() {
    if (!invoice.id) return;
    setLoading(true);
    try {
      const data = await invoiceService.listPayments(invoice.id);
      setPayments(data || []);
      
      // Reload invoice to get updated status
      const updatedInvoice = await invoiceService.get(invoice.id);
      if (updatedInvoice && updateField) {
        // Update invoice status if it changed
        if (updatedInvoice.status !== invoice.status) {
          updateField('status', updatedInvoice.status);
        }
        // Update balance fields
        updateField('totalPaid', updatedInvoice.total_paid || updatedInvoice.totalPaid || 0);
        updateField('balanceDue', updatedInvoice.balance_due || updatedInvoice.balanceDue || 0);
      }
    } catch (err) {
      console.error("Failed to load payments", err);
    } finally {
      setLoading(false);
    }
  }

  const totalPaid = payments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  const balanceDue = Math.max(0, total - totalPaid);
  const paymentStatus =
    total > 0
      ? balanceDue <= 0
        ? "paid"
        : totalPaid > 0
        ? "partial"
        : "unpaid"
      : "unpaid";

  // Update invoice balance when payments change
  useEffect(() => {
    if (onPaymentUpdate) {
      onPaymentUpdate({ totalPaid, balanceDue, paymentStatus });
    }
  }, [totalPaid, balanceDue, paymentStatus]);

  return (
    <section className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium text-slate-900">Payments</h2>
        {invoice.id ? (
          <button
            onClick={() => setShowAddPayment(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition"
            style={{
              background: "var(--color-primary)",
              color: "white",
            }}
          >
            <Plus size={16} />
            Add Payment
          </button>
        ) : (
          <span className="text-xs text-[var(--color-text-muted)]">Save invoice to add payments</span>
        )}
      </div>

      {/* Payment Status Summary */}
      <div
        className="rounded-xl p-4 border"
        style={{
          background: "var(--color-bg-muted)",
          borderColor: "var(--color-border)",
        }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <PaymentStatusIcon status={paymentStatus} />
            <span className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
              {paymentStatus === "paid"
                ? "Fully Paid"
                : paymentStatus === "partial"
                ? "Partially Paid"
                : "Unpaid"}
            </span>
          </div>
          <div className="text-right">
            <div className="text-xs" style={{ color: "var(--color-text-muted)" }}>
              Total Paid
            </div>
            <div className="text-lg font-semibold" style={{ color: "var(--color-text-primary)" }}>
              ₹ {totalPaid.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        {total > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs" style={{ color: "var(--color-text-muted)" }}>
              <span>Balance Due: ₹ {balanceDue.toFixed(2)}</span>
              <span>{Math.round((totalPaid / total) * 100)}% paid</span>
            </div>
            <div
              className="h-2 rounded-full overflow-hidden"
              style={{ background: "var(--color-bg-canvas)" }}
            >
              <div
                className="h-full transition-all duration-300 rounded-full"
                style={{
                  width: `${Math.min(100, (totalPaid / total) * 100)}%`,
                  background:
                    paymentStatus === "paid"
                      ? "var(--color-success)"
                      : paymentStatus === "partial"
                      ? "var(--color-warning)"
                      : "var(--color-border)",
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Payment Timeline */}
      {loading ? (
        <div className="text-sm text-[var(--color-text-muted)]">Loading payments…</div>
      ) : payments.length > 0 ? (
        <div className="space-y-2">
          {payments.map((payment, index) => (
            <PaymentRow
              key={payment.id}
              payment={payment}
              onDelete={async () => {
                if (!confirm("Delete this payment?")) return;
                try {
                  await invoiceService.deletePayment(payment.id);
                  await loadPayments();
                } catch (err) {
                  console.error("Failed to delete payment", err);
                }
              }}
            />
          ))}
        </div>
      ) : (
        <div
          className="rounded-lg p-8 text-center border border-dashed"
          style={{
            background: "var(--color-bg-canvas)",
            borderColor: "var(--color-border)",
          }}
        >
          <div className="text-sm" style={{ color: "var(--color-text-muted)" }}>
            No payments recorded yet
          </div>
        </div>
      )}

      {/* Add Payment Drawer - Global Overlay */}
      {invoice.id && (
        <AddPaymentDrawer
          isOpen={showAddPayment}
          invoiceId={invoice.id}
          balanceDue={balanceDue}
          onClose={() => setShowAddPayment(false)}
          onSaved={async () => {
            await loadPayments();
            setShowAddPayment(false);
          }}
        />
      )}
    </section>
  );
}

/* ───────── Payment Row ───────── */

function PaymentRow({ payment, onDelete }) {
  const [showDelete, setShowDelete] = useState(false);

  return (
    <div
      className="flex items-center justify-between p-3 rounded-lg border group"
      style={{
        background: "var(--color-bg-canvas)",
        borderColor: "var(--color-border)",
      }}
      onMouseEnter={() => setShowDelete(true)}
      onMouseLeave={() => setShowDelete(false)}
    >
      <div className="flex items-center gap-3 flex-1">
        <div
          className="w-2 h-2 rounded-full shrink-0"
          style={{ background: "var(--color-success)" }}
        />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
            ₹ {Number(payment.amount).toFixed(2)}
          </div>
          <div className="flex items-center gap-2 text-xs" style={{ color: "var(--color-text-muted)" }}>
            <span>{formatDate(payment.payment_date)}</span>
            {payment.mode && (
              <>
                <span>•</span>
                <span className="capitalize">{payment.mode}</span>
              </>
            )}
            {payment.notes && (
              <>
                <span>•</span>
                <span className="truncate">{payment.notes}</span>
              </>
            )}
          </div>
        </div>
      </div>

      {showDelete && (
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          onClick={onDelete}
          className="p-1.5 rounded-md hover:bg-red-50 text-red-600 transition"
        >
          <Trash2 size={14} />
        </motion.button>
      )}
    </div>
  );
}

/* ───────── Add Payment Drawer ───────── */

function AddPaymentDrawer({ isOpen, invoiceId, balanceDue, onClose, onSaved }) {
  const [form, setForm] = useState({
    amount: balanceDue > 0 ? balanceDue : 0,
    payment_date: new Date().toISOString().slice(0, 10),
    mode: "cash",
    notes: "",
  });
  const [saving, setSaving] = useState(false);

  // Reset form when drawer opens with new balanceDue
  useEffect(() => {
    if (isOpen) {
      setForm({
        amount: balanceDue > 0 ? balanceDue : 0,
        payment_date: new Date().toISOString().slice(0, 10),
        mode: "cash",
        notes: "",
      });
    }
  }, [isOpen, balanceDue]);

  async function handleSave() {
    if (!form.amount || form.amount <= 0) {
      alert("Please enter a valid payment amount");
      return;
    }

    setSaving(true);
    try {
      await invoiceService.addPayment(invoiceId, form);
      onSaved();
    } catch (err) {
      console.error("Failed to add payment", err);
      alert("Failed to add payment. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  const footer = (
    <div className="flex justify-end gap-3">
      <button
        onClick={onClose}
        disabled={saving}
        className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors disabled:opacity-50"
      >
        Cancel
      </button>
      <button
        onClick={handleSave}
        disabled={saving || !form.amount || form.amount <= 0}
        className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {saving ? "Saving..." : "Add Payment"}
      </button>
    </div>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Add Payment"
      subtitle="Record a payment for this invoice"
      footer={footer}
      width="460px"
    >
      <div className="space-y-5">
        {/* Amount */}
        <div className="space-y-2.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Amount
          </label>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, amount: balanceDue }))}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
            >
              Pay full ₹{balanceDue.toFixed(2)}
            </button>
            <button
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, amount: balanceDue / 2 }))}
              className="px-3 py-1.5 text-xs font-medium rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
            >
              50% ₹{(balanceDue / 2).toFixed(2)}
            </button>
          </div>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-slate-500">
              ₹
            </span>
            <input
              type="number"
              value={form.amount || ""}
              onChange={(e) => setForm({ ...form, amount: Number(e.target.value) || 0 })}
              min={0}
              max={balanceDue}
              className="w-full pl-8 pr-4 py-2.5 rounded-lg border border-slate-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
              placeholder="0.00"
            />
          </div>
          <div className="text-xs text-slate-500">
            Balance due: ₹{balanceDue.toFixed(2)}
          </div>
        </div>

        {/* Payment Date */}
        <div className="space-y-2.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Payment Date
          </label>
          <input
            type="date"
            value={form.payment_date}
            onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-shadow"
          />
        </div>

        {/* Payment Mode */}
        <div className="space-y-2.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Payment Method
          </label>
          <div className="grid grid-cols-2 gap-2">
            {["cash", "bank_transfer", "cheque", "upi", "card", "other"].map((mode) => (
              <button
                key={mode}
                onClick={() => setForm({ ...form, mode })}
                className={`px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  form.mode === mode
                    ? "bg-indigo-600 text-white shadow-sm"
                    : "bg-slate-50 text-slate-700 hover:bg-slate-100 border border-slate-200"
                }`}
              >
                {formatPaymentMode(mode)}
              </button>
            ))}
          </div>
        </div>

        {/* Notes */}
        <div className="space-y-2.5">
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Notes <span className="text-slate-400 font-normal">(Optional)</span>
          </label>
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            rows={3}
            className="w-full px-3 py-2.5 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none transition-shadow"
            placeholder="Payment reference, transaction ID, etc."
          />
        </div>
      </div>
    </Drawer>
  );
}

/* ───────── Helpers ───────── */

function PaymentStatusIcon({ status }) {
  const props = { size: 18 };
  switch (status) {
    case "paid":
      return <CheckCircle2 {...props} style={{ color: "var(--color-success)" }} />;
    case "partial":
      return <Clock {...props} style={{ color: "var(--color-warning)" }} />;
    default:
      return <XCircle {...props} style={{ color: "var(--color-text-muted)" }} />;
  }
}

function formatDate(dateString) {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatPaymentMode(mode) {
  const modes = {
    cash: "Cash",
    bank_transfer: "Bank Transfer",
    cheque: "Cheque",
    upi: "UPI",
    card: "Card",
    other: "Other",
  };
  return modes[mode] || mode;
}

